import { useMemo } from 'react'

import { difference, keyBy, union, xor } from 'lodash'

import ColumnsConfigPanel, { ColumnsSummary } from './Panel'
import { DEFAULT_COLUMNS_CONFIG, alwaysPinnedKeys, canReorder, isAlwaysPinned, resolvePinned, togglePinColumn } from './config'
import { reorderWithinSection, resolveColumnOrder } from './utils'

export default function ColumnsConfigEditor({
  catalog = [], fields = [], onChangeFields,
  columnsConfig = DEFAULT_COLUMNS_CONFIG, onChangeColumnsConfig,
}) {
  const locked = useMemo(() => catalog.filter(item => !canReorder(item)), [catalog])
  const keys = useMemo(() => catalog.filter(canReorder).map(({ key }) => key), [catalog])
  const orderKeys = useMemo(() => resolveColumnOrder(columnsConfig.order, keys), [columnsConfig.order, keys])
  const alwaysPinned = useMemo(() => alwaysPinnedKeys(catalog), [catalog])
  const pinned = useMemo(() => resolvePinned(columnsConfig, alwaysPinned), [columnsConfig, alwaysPinned])

  const items = useMemo(() => {
    const byKey = keyBy(catalog, 'key')
    return [...locked, ...orderKeys.map(key => byKey[key])].filter(Boolean).map(item => ({
      ...item,
      pinned: pinned.includes(item.key),
      checked: isAlwaysPinned(item) || (item.isField ? fields.includes(item.key) : !columnsConfig.hidden.includes(item.key)),
    }))
  }, [catalog, locked, orderKeys, pinned, fields, columnsConfig.hidden])

  const toggleChecked = key => {
    const item = catalog.find(current => current.key === key)
    if (!item || isAlwaysPinned(item)) {
      return
    }

    if (item.isField) {
      onChangeFields(xor(fields, [key]))
      return
    }

    onChangeColumnsConfig({ ...columnsConfig, hidden: xor(columnsConfig.hidden, [key]) })
  }

  const setChecked = (keys, checked) => {
    const targets = catalog.filter(item => keys.includes(item.key) && !isAlwaysPinned(item))
    const fieldKeys = targets.filter(item => item.isField).map(({ key }) => key)
    const fixedKeys = targets.filter(item => !item.isField).map(({ key }) => key)

    if (fieldKeys.length) {
      onChangeFields(checked ? union(fields, fieldKeys) : difference(fields, fieldKeys))
    }

    if (fixedKeys.length) {
      const hidden = checked ? difference(columnsConfig.hidden, fixedKeys) : union(columnsConfig.hidden, fixedKeys)
      onChangeColumnsConfig({ ...columnsConfig, hidden })
    }
  }

  const togglePinned = key => onChangeColumnsConfig(togglePinColumn(columnsConfig, catalog, key))

  const reorder = (sectionItems, nextItems) => {
    const strip = list => list.filter(canReorder)
    onChangeColumnsConfig({ ...columnsConfig, order: reorderWithinSection(orderKeys, strip(sectionItems), strip(nextItems)) })
  }

  return <ColumnsConfigPanel
    items={items}
    header={<ColumnsSummary items={items} />}
    onReorder={reorder}
    onTogglePinned={togglePinned}
    onToggleChecked={toggleChecked}
    onSetChecked={setChecked}
  />
}
